import React, { useCallback, useMemo, useRef, useState } from 'react';
import ChatAssistantRouter from './ChatAssistantRouter';
import { useResponsive } from '../hooks/useResponsive';
import AssistantFrame from './shell/AssistantFrame';
import { useFloatingWindow } from './hooks/useFloatingWindow';
import { useSidebarWidth } from './hooks/useSidebarWidth';
import { useBodyInset } from './hooks/useBodyInset';
import {
    ASSISTANT_MODES,
    FLOATING_ANCHOR_OFFSET,
    DOCK_PANEL_WIDTH,
    DOCK_PANEL_HEIGHT,
    DOCK_BOTTOM_OFFSET,
} from './config/modes';
import { MarkdownSizeProvider } from '../../components/markdown/MarkdownSizeContext';
import { DockExpansionContext } from './shell/DockExpansionContext';

const WRAPPER_BASE = 'fixed bg-app-bg flex flex-col overflow-hidden';

const wrapperClassFor = (mode, side) => {
    if (mode === ASSISTANT_MODES.FRAMLESS) {
        return 'relative w-full h-full bg-app-bg flex flex-col overflow-hidden';
    }
    if (mode === ASSISTANT_MODES.SIDEBAR) {
        const sideClass = side === 'left' ? 'left-0 border-r' : 'right-0 border-l';
        return `${WRAPPER_BASE} top-0 bottom-0 z-30 border-gray-200 ${sideClass}`;
    }
    if (mode === ASSISTANT_MODES.DOCK) {
        return `${WRAPPER_BASE} left-1/2 z-30 rounded-2xl dock-shadow dock-panel-animate dock-fill-width`;
    }
    return `${WRAPPER_BASE} z-30 border border-gray-200 rounded-2xl shadow-2xl`;
};

export default function AssistantShell({
    mode,
    onModeChange,
    onClose,
    side = 'right',
    resetKey,
    cacheKey,
}) {
    const { isMobile } = useResponsive();

    const floating = useFloatingWindow();
    const sidebar = useSidebarWidth({ side });

    const isSidebar = mode === ASSISTANT_MODES.SIDEBAR;
    const isFloating = mode === ASSISTANT_MODES.FLOATING;
    const isDock = mode === ASSISTANT_MODES.DOCK;
    const isFrameless = mode === ASSISTANT_MODES.FRAMLESS;

    useBodyInset({ side, width: sidebar.width, enabled: isSidebar && !isMobile });

    const [dockExpanded, setDockExpanded] = useState(false);
    const dockExpansionValue = useMemo(
        () => ({ setDockExpanded: isDock ? setDockExpanded : () => {} }),
        [isDock],
    );

    const wrapperRef = useRef(null);
    const handleDockFocus = useCallback(
        (event) => {
            if (!isDock) return;
            const tag = event.target?.tagName;
            if (tag !== 'INPUT' && tag !== 'TEXTAREA' && !event.target?.isContentEditable) return;
            const el = wrapperRef.current;
            if (!el) return;
            el.classList.remove('dock-focus-glow');
            void el.offsetWidth;
            el.classList.add('dock-focus-glow');
        },
        [isDock],
    );

    const dockHeight = dockExpanded
        ? `min(90vh, calc(100vh - 24px))`
        : `min(${DOCK_PANEL_HEIGHT}px, calc(100vh - 24px))`;

    const wrapperStyle = isFrameless
        ? {}
        : isDock
        ? {
              bottom: DOCK_BOTTOM_OFFSET,
              width: `min(${DOCK_PANEL_WIDTH}px, calc(100vw - 16px))`,
              height: dockHeight,
              transition: 'height 320ms cubic-bezier(0.22, 1, 0.36, 1)',
              '--header-height': '16px',
          }
        : isFloating
            ? {
                  right: FLOATING_ANCHOR_OFFSET,
                  bottom: FLOATING_ANCHOR_OFFSET,
                  width: floating.size.width,
                  height: floating.size.height,
                  '--header-height': '16px',
              }
            : isSidebar
                ? { 
                    width: isMobile ? 'min(400px, 100vw)' : sidebar.width, 
                    top: isMobile ? 'var(--header-height)' : '0px',
                    '--header-height': '16px' 
                  }
                : { '--header-height': '16px' };

    const content = (
        <MarkdownSizeProvider size="sm">
            <DockExpansionContext.Provider value={dockExpansionValue}>
                <ChatAssistantRouter key={resetKey} forceNewSession={resetKey != null && !cacheKey} cacheKey={cacheKey}>
                    <AssistantFrame mode={mode} onModeChange={onModeChange} onMinimize={onClose} />
                </ChatAssistantRouter>
            </DockExpansionContext.Provider>
        </MarkdownSizeProvider>
    );

    return (
        <>
            {isSidebar && isMobile && (
                <div 
                    className="fixed inset-0 bg-black/60 z-[35] transition-opacity"
                    style={{ top: 'var(--header-height)' }}
                    onClick={onClose}
                    aria-hidden="true"
                />
            )}
            <div
                ref={wrapperRef}
                className={wrapperClassFor(mode, side)}
                style={isFrameless ? wrapperStyle : { ...wrapperStyle, zIndex: 40 }}
                onFocus={isDock ? handleDockFocus : undefined}
            >
                {isSidebar && !isMobile && (
                    <div
                        onMouseDown={sidebar.startResize}
                        className={`absolute top-0 bottom-0 w-1 cursor-ew-resize hover:bg-blue-400/50 z-10 ${side === 'right' ? 'left-0 -translate-x-px' : 'right-0 translate-x-px'}`}
                    />
                )}

                {isFloating && (
                    <div
                        onMouseDown={floating.startResize}
                        className="absolute top-0 left-0 w-4 h-4 cursor-nwse-resize z-20"
                        aria-label="Resize"
                    />
                )}

                {isDock ? (
                    <>
                        <div className="dock-beam-ring" aria-hidden="true" />
                        <div className="dock-content-inset">{content}</div>
                    </>
                ) : (
                    content
                )}
            </div>
        </>
    );
}
